import { useEffect, useState } from 'react';
import { ExternalLink, CheckCircle2, DollarSign, RefreshCw, AlertTriangle } from 'lucide-react';
import supabase from '../lib/supabase';
import {
  PageHeader, Section, Badge, Button, EmptyState, StatCard, StatusLine,
} from '../components/ui';

type Coach = {
  id: string;
  stripe_account_id: string | null;
  stripe_onboarding_completed_at: string | null;
  type: string;
};

type Transfer = {
  id: string;
  amount: number;
  currency: string;
  status: string;
  created_at: string;
  stripe_transfer_id: string | null;
  failure_reason: string | null;
};

export default function CoachStripe({ coachId }: { coachId: string }) {
  const [coach, setCoach] = useState<Coach | null>(null);
  const [transfers, setTransfers] = useState<Transfer[]>([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  const load = async () => {
    const [c, t] = await Promise.all([
      supabase.from('coaches').select('id, stripe_account_id, stripe_onboarding_completed_at, type').eq('id', coachId).single(),
      supabase.from('stripe_transfers')
        .select('id, amount, currency, status, created_at, stripe_transfer_id, failure_reason')
        .eq('coach_id', coachId)
        .order('created_at', { ascending: false })
        .limit(50),
    ]);
    setCoach((c.data as Coach) ?? null);
    setTransfers((t.data as Transfer[]) ?? []);
  };

  useEffect(() => { load(); }, [coachId]);

  const startOnboarding = async () => {
    setBusy(true);
    setMsg(null);
    const { data, error } = await supabase.functions.invoke('stripe-connect-onboarding', {
      body: { coachId },
    });
    setBusy(false);
    if (error || !data?.url) {
      setMsg({ kind: 'error', text: (error as any)?.message || 'Could not start onboarding.' });
      return;
    }
    window.open(data.url, '_blank', 'noopener,noreferrer');
  };

  const refresh = async () => {
    setBusy(true);
    setMsg(null);
    const { error } = await supabase.functions.invoke('refresh-stripe-connect', { body: { coachId } });
    setBusy(false);
    if (error) setMsg({ kind: 'error', text: (error as any).message || 'Refresh failed.' });
    else { setMsg({ kind: 'success', text: 'Stripe status refreshed.' }); load(); }
  };

  if (!coach) return <div className="p-8 text-sm text-gray-500">Loading…</div>;

  const ready = !!coach.stripe_onboarding_completed_at;

  // Totals for the stat cards.
  const paid30d = transfers
    .filter((t) => t.status === 'paid' && new Date(t.created_at) >= new Date(Date.now() - 30 * 86400_000))
    .reduce((s, t) => s + Number(t.amount), 0);
  const pendingCount = transfers.filter((t) => t.status !== 'paid').length;
  const lastTransfer = transfers[0];
  const currency = transfers[0]?.currency ?? 'AED';

  return (
    <div className="p-8 max-w-4xl">
      <PageHeader
        eyebrow="Coach"
        title="Stripe payouts"
        subtitle="We send your share to Stripe after every paid lesson. Stripe then pays out to your bank on the schedule you set in the dashboard."
      />

      {msg && <div className="mb-4"><StatusLine kind={msg.kind}>{msg.text}</StatusLine></div>}

      <Section
        title="Connect status"
        subtitle="You can take lessons only after onboarding is complete."
      >
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            {ready ? (
              <Badge tone="green" icon={CheckCircle2}>Connected · ready to receive payouts</Badge>
            ) : coach.stripe_account_id ? (
              <Badge tone="amber" icon={AlertTriangle}>KYC pending</Badge>
            ) : (
              <Badge tone="gray">Not started</Badge>
            )}
            {coach.stripe_account_id && (
              <div className="text-[11px] text-gray-400 mt-2 font-mono truncate">
                {coach.stripe_account_id}
              </div>
            )}
          </div>
          <div className="flex gap-2">
            {!ready && (
              <Button icon={ExternalLink} loading={busy} onClick={startOnboarding}>
                {coach.stripe_account_id ? 'Continue KYC' : 'Start onboarding'}
              </Button>
            )}
            {coach.stripe_account_id && (
              <Button variant="secondary" icon={RefreshCw} loading={busy} onClick={refresh}>
                Refresh status
              </Button>
            )}
          </div>
        </div>
      </Section>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <StatCard icon={DollarSign} label="Paid out (30d)" value={`${currency} ${paid30d.toFixed(0)}`} />
        <StatCard icon={RefreshCw}  label="Pending transfers" value={pendingCount} />
        <StatCard icon={CheckCircle2} label="Last transfer"
          value={lastTransfer
            ? new Date(lastTransfer.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
            : '—'} />
      </div>

      <Section title="Recent transfers" subtitle="Last 50 transactions." className="p-0 overflow-hidden">
        {transfers.length === 0 ? (
          <EmptyState
            icon={DollarSign}
            title="No transfers yet"
            description="Your payouts will appear here after your first paid lesson."
          />
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50/70 text-[11px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Date</th>
                <th className="text-right px-5 py-3 font-semibold">Amount</th>
                <th className="text-left px-5 py-3 font-semibold">Status</th>
                <th className="text-left px-5 py-3 font-semibold">Stripe ID</th>
              </tr>
            </thead>
            <tbody>
              {transfers.map((t) => (
                <tr key={t.id} className="border-t border-gray-100 hover:bg-gray-50/60">
                  <td className="px-5 py-3 text-gray-700 whitespace-nowrap">
                    {new Date(t.created_at).toLocaleString('en-GB', {
                      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
                    })}
                  </td>
                  <td className="px-5 py-3 text-right font-medium">
                    {t.currency} {Number(t.amount).toFixed(2)}
                  </td>
                  <td className="px-5 py-3">
                    <Badge tone={
                      t.status === 'paid' ? 'green'
                      : t.status === 'failed' ? 'red'
                      : t.status === 'reversed' ? 'red'
                      : 'amber'
                    }>{t.status}</Badge>
                    {t.failure_reason && (
                      <div className="text-[11px] text-red-700 mt-1 max-w-xs">{t.failure_reason}</div>
                    )}
                  </td>
                  <td className="px-5 py-3 font-mono text-[11px] text-gray-500">
                    {t.stripe_transfer_id ?? '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>
    </div>
  );
}
